import React from "react";
import { useQuery } from "@apollo/client";
import { useLocation } from "react-router-dom";
import { IconButton, Tooltip, List as MuiList } from "@mui/material";
import { Person, Language, ContactMail, Description, Add, FolderOpen } from "@mui/icons-material";
import SidebarListItem from "components/common/navigation/sidebar-list-item";
import Development from "components/development";
import { authorizeForRole } from "components/auth/authorize-for-role";
import { Viewer } from "components/auth/types";
import { useFeature } from "components/feature";
import { GET_PLANS_ALL } from "pages/dashboard/admin/plans/queries";
import {
  Feature,
  UserRole,
  plansAll,
} from "types/gql-types";
import { SettingsHeader } from "./pages/settings-header";
import ListsSidebar from "./lists-sidebar";


const SETTINGS_PAGES = [
  {
    name: "Organization",
    icon: "fa-globe",
    path: "organization",
    supervisor: true,
    released: true,
  },
  {
    name: "Team",
    icon: "fa-user",
    path: "team",
    supervisor: true,
    released: true,
  },
  {
    name: "Schedule List Update",
    icon: "fa-envelope",
    path: "scheduled-updates",
    supervisor: true,
    released: true,
  },
  {
    name: "Imports",
    icon: "fa-folder-open",
    path: "imports/upload",
    supervisor: false,
    released: Feature.IMPORTS,
  },
  {
    name: "Templates",
    icon: "fa-file",
    path: "templates",
    supervisor: false,
    released: true,
  },
  {
    name: "Contracts",
    icon: "fa-file",
    path: "contracts",
    supervisor: false,
    supervisorOrAdmin: true,
    released: Feature.PLAN_SELECTION,
  },
] as const;

const icons = {
  "fa-globe": Language,
  "fa-user": Person,
  "fa-envelope": ContactMail,
  "fa-file": Description,
  "fa-folder-open": FolderOpen,
} as const;

type NavLinkProps = {
  path: string;
  icon: keyof typeof icons;
  text: string;
}

const NavLink = (props: NavLinkProps) => {
  const location = useLocation();
  const Icon = icons[props.icon];
  const href = `/dash/manager/settings/${props.path}`;
  return (
    <SidebarListItem
      icon={<Icon />}
      text={props.text}
      selected={!!location.pathname.includes(props.path)}
      href={href}
    />
  );
};

// Resolved-empty (never mid-load) tells us the org has no public plans.
const useHasNoPublicPlans = (enabled: boolean): boolean => {
  const { data, loading } = useQuery<plansAll>(GET_PLANS_ALL, {
    skip: !enabled,
    fetchPolicy: "cache-and-network",
  });
  return !loading && !(data?.plansAll || []).some((p) => p?.isPublic);
};

const Sidebar: React.FC<{viewer: Viewer}> = (props) => {
  const planSelectionEnabled = useFeature(Feature.PLAN_SELECTION);
  const importsEnabled = useFeature(Feature.IMPORTS);
  const canAddProperty = Boolean(props.viewer?.isSupervisor && planSelectionEnabled);
  const hasNoPublicPlans = useHasNoPublicPlans(canAddProperty);
  return (
    <div>
      <SettingsHeader title="Settings" />
      <MuiList>
        <Development>
          {(development) => SETTINGS_PAGES.map((attrs) => {
            const isAuthorized =
              "supervisorOrAdmin" in attrs && attrs.supervisorOrAdmin ? Boolean(props.viewer?.isSupervisor || props.viewer?.isAdmin) : attrs.supervisor === false || Boolean(props.viewer?.isSupervisor);
            const isReleased =
            attrs.released === true ||
            development ||
            (attrs.released === Feature.IMPORTS && importsEnabled) ||
            (attrs.released === Feature.PLAN_SELECTION && planSelectionEnabled);
            return (
              isAuthorized &&
            isReleased && (
                <NavLink
                  key={attrs.name}
                  text={attrs.name}
                  icon={attrs.icon}
                  path={attrs.path}
                />
              )
            );
          })}
        </Development>
      </MuiList>
      {props.viewer && (
        <div>
          <SettingsHeader title="Properties">
            {canAddProperty && !hasNoPublicPlans && (
              <Tooltip title="Add" placement="right">
                <IconButton size="small" aria-label="Add property"
                  href="/dash/manager/settings/property/new">
                  <Add />
                </IconButton>
              </Tooltip>
            )}
          </SettingsHeader>
          <ListsSidebar viewer={props.viewer} />
        </div>
      )}
    </div>
  );
};

export default authorizeForRole<Record<string, unknown>>(
  [UserRole.PROPERTY_MANAGER],
  Sidebar as unknown as React.FC<Record<string, unknown>>,
);

// Un-wrapped component for tests: authorizeForRole needs a live auth context.
export const testExports = { Sidebar };
